import { useState } from 'react';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { addDaysISO, formatISODay, formatTime, humanISODay, todayISO, type TimeFormat } from '../../lib/time';
import { plural } from '../../lib/plural';
import { Avatar, Pill } from '../../ui/parts';
import { useAgenda, useReplyReferral, useStaffReferrals } from '../api';
import { AppointmentSheet } from '../AppointmentSheet';
import type { AgendaItem, Referral } from '../types';

/** Вкладка «Мой день»: входящие заявки, счётчики и лента приёмов. */
export function Today() {
  const me = useMeContext();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;
  const [date, setDate] = useState(() => todayISO(tz));
  const agenda = useAgenda(date);
  const sheet = useSheet();

  useScreen('Мой день', null);

  const items = agenda.data?.items ?? [];
  const live = items.filter((a) => a.status !== 'cancelled' && a.status !== 'no_show');
  const unpaid = live.filter((a) => a.payment_status !== 'paid');
  const gaps = gapsBetween(items);

  return (
    <Screen>
      <Inbox />

      <div className="daynav">
        <button type="button" aria-label="Предыдущий день" onClick={() => setDate(addDaysISO(date, -1))}>‹</button>
        <b>{humanISODay(date, tz)}</b>
        <button type="button" aria-label="Следующий день" onClick={() => setDate(addDaysISO(date, 1))}>›</button>
      </div>

      <div className="stats">
        <div className="stat">
          <b>{live.length}</b>
          <span>{plural(live.length, 'запись', 'записи', 'записей')}</span>
        </div>
        <div className="stat">
          <b>{gaps.length}</b>
          <span>{plural(gaps.length, 'окно', 'окна', 'окон')}</span>
        </div>
        <div className="stat">
          <b>{unpaid.length}</b>
          <span>без оплаты</span>
        </div>
      </div>

      <p className="eyebrow">Расписание дня</p>
      {agenda.isPending ? <p className="lead">Загружаем день…</p> : null}
      {!agenda.isPending && items.length === 0 ? (
        <p className="lead">{formatISODay(date)} записей нет.</p>
      ) : null}

      <div className="timeline">
        {items.map((a, i) => (
          <Row key={a.id} item={a} gapAfter={gaps.find((g) => g.afterIndex === i)} onOpen={() => sheet.open(<AppointmentSheet item={a} />)} tz={tz} tf={tf} />
        ))}
      </div>
    </Screen>
  );
}

function Row({ item, gapAfter, onOpen, tz, tf }: {
  item: AgendaItem;
  gapAfter?: Gap;
  onOpen: () => void;
  tz: string;
  tf: TimeFormat;
}) {
  return (
    <>
      <button className={`tl${item.status === 'cancelled' ? ' gap' : ''}`} onClick={onOpen}>
        <span className="tl-time">{formatTime(item.starts_at_utc, tz, tf)}</span>
        <span className="tl-body">
          <b>{item.child_name}</b>
          <span>
            {item.service_title}
            {item.my_note ? <> · <b style={{ fontWeight: 600 }}>заметка</b></> : null}
          </span>
        </span>
        <span className="tl-side">{badge(item)}</span>
      </button>
      {gapAfter ? (
        <div className="tl gap">
          <span className="tl-time">{formatTime(gapAfter.from, tz, tf)}</span>
          <span className="tl-body">
            <b>Перерыв · {gapAfter.minutes} мин</b>
          </span>
        </div>
      ) : null}
    </>
  );
}

function badge(a: AgendaItem) {
  if (a.status === 'no_show') return <Pill kind="off">не пришёл</Pill>;
  if (a.status === 'cancelled') return <Pill kind="off">отменён</Pill>;
  if (a.status === 'completed') return <Pill kind="done">проведён</Pill>;
  return a.payment_status === 'paid' ? <Pill kind="paid">Оплачено</Pill> : <Pill kind="await">Без оплаты</Pill>;
}

/** Заявки, где родитель или коллега указали именно этого специалиста. */
function Inbox() {
  const referrals = useStaffReferrals();
  const sheet = useSheet();
  const list = referrals.data ?? [];
  if (!list.length) return null;

  return (
    <div>
      <p className="eyebrow">Заявки на вас</p>
      <div className="rows" style={{ margin: '10px 0 16px' }}>
        {list.map((r) => (
          <button className="rw" key={r.id} onClick={() => sheet.open(<ReferralSheet referral={r} />)}>
            <Avatar name={r.child_name} small />
            <span className="rw-body">
              <b>{r.child_name}</b>
              <span>{r.note || 'без комментария'}</span>
            </span>
            {r.specialist_reply === 'accepted' ? <Pill kind="paid">беру</Pill>
              : r.specialist_reply === 'declined' ? <Pill kind="off">отказ</Pill>
              : <Pill kind="await">новая</Pill>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Ответ на заявку не создаёт закрепление: закрепляет администратор. */
function ReferralSheet({ referral }: { referral: Referral }) {
  const sheet = useSheet();
  const toast = useToast();
  const reply = useReplyReferral();

  const answer = (v: 'accepted' | 'declined') =>
    reply.mutate(
      { id: referral.id, reply: v },
      {
        onSuccess: () => {
          toast(v === 'accepted' ? 'Ответили: готовы взять' : 'Ответили: не ваш профиль');
          sheet.close();
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось ответить'),
      },
    );

  return (
    <>
      <h3>Заявка · {referral.child_name}</h3>
      <p>
        {referral.from_specialist_id ? 'От коллеги' : 'От родителя, через приложение'}.
        Закрепляет администратор — вы отвечаете, беретесь ли.
      </p>
      <div className="kv">
        <div>
          <span className="k">Запрос</span>
          <span className="v" style={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{referral.note || 'не указан'}</span>
        </div>
        <div>
          <span className="k">Родитель</span>
          <span className="v">{referral.parent_name} · {referral.phone}</span>
        </div>
      </div>

      {referral.specialist_reply ? (
        <>
          <p className="lead">
            Вы ответили: {referral.specialist_reply === 'accepted' ? 'готовы взять' : 'не ваш профиль'}.
            Администратор видит ответ.
          </p>
          <button className="btn" onClick={() => sheet.close()}>Закрыть</button>
        </>
      ) : (
        <>
          <div className="row-actions">
            <button className="btn ghost-danger" disabled={reply.isPending} onClick={() => answer('declined')}>Не мой профиль</button>
            <button className="btn solid" disabled={reply.isPending} onClick={() => answer('accepted')}>Готов взять</button>
          </div>
          <button className="btn" onClick={() => sheet.close()}>Решу позже</button>
        </>
      )}
    </>
  );
}

type Gap = { afterIndex: number; from: number; minutes: number };

/**
 * Перерыв между двумя соседними приёмами — ровно то, что видно в дне, и ничего
 * больше. Свободные окна для записи считает сервер: здесь мы не знаем ни буфера,
 * ни занятости ребёнка у коллег.
 */
function gapsBetween(items: AgendaItem[]): Gap[] {
  const live = items.filter((a) => a.status !== 'cancelled');
  const out: Gap[] = [];
  for (let i = 0; i < live.length - 1; i++) {
    const minutes = Math.round((live[i + 1].starts_at_utc - live[i].ends_at_utc) / 60_000);
    if (minutes >= 30) out.push({ afterIndex: items.indexOf(live[i]), from: live[i].ends_at_utc, minutes });
  }
  return out;
}
